import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { TimerCardWrapper, TimerContainer } from '../../assets/styles/components/timerCard.styled';
import CardData from '../dashboard/cardData';
import {
  Briefcase,
  CircleTick,
  Clipboard,
  CloseCircle,
  Edit,
  HorizontalDots,
  Reset,
  TimerAdd,
  TimerRemove,
  Pause,
  Play,
  Delete,
} from '../icons';
import { Flex } from 'antd';
import { Button, Divider, IconButton, Text, Tooltip } from '../Ant';
import { ITimer } from '../../interface/timer';
import { LOCAL_STORAGE_NAMES } from '../../constants/constants';
import { computeInitialDuration, formatTime } from '../../constants/utilityFunctions';
import { formatMatterReference } from '../../constants/matter';
import ConfirmModal from '../confirmationmodal/confirmationModal';
import { useTimerActions } from '../../hooks/useTimerActions';

const TimerCard = ({ timerValue, refetch }: { timerValue: ITimer; refetch: () => void }) => {
  const timers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const timer = timers.find((t) => t.id === timerValue.id);
  const { t } = useTranslation();
  const [isRunning, setIsRunning] = useState(timer?.isRunning ?? false);
  const [seconds, setSeconds] = useState(computeInitialDuration(timer?.id ?? '') ?? 0);
  const [intervalStart, setIntervalStart] = useState<string | null>(timer?.intervalStart ?? null);
  const [resetTimerConfirmation, setResetTimerConfirmation] = useState<boolean>(false);
  const [deleteTimerConfirmation, setDeleteTimerConfirmation] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const {
    handleAddFiveMinutes,
    handleAddTransaction,
    handleRemoveTimer,
    handleRemoveFiveMinutes,
    handleEdit,
    toggleTimer,
    handleReset,
    handleTimerDetail,
  } = useTimerActions({
    timer,
    timers,
    intervalStart,
    setIsRunning,
    setIntervalStart,
    setSeconds,
    refetch,
  });

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const baseDuration = timer?.duration ?? 0;
        const start = intervalStart ? new Date(intervalStart).getTime() : Date.now();
        const delta = Math.floor((now - start) / 1000);
        const total = baseDuration + delta;
        setSeconds(Math.max(total, 0));
      }, 1000);
    } else {
      setSeconds(timer?.duration ?? 0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, intervalStart]);
  useEffect(() => {
    setIsRunning(timer?.isRunning ?? false);
  }, [timer?.isRunning]);

  const handleTimerReset = () => {
    handleReset();
    setResetTimerConfirmation(false);
  };
  return (
    <>
      <TimerCardWrapper vertical gap={10}>
        <Flex>
          <CardData
            type="subject"
            icon={<Clipboard />}
            title={t('common:subject')}
            content={timer?.subject ?? '-'}
            contentclass="lineclamp-1"
          />
          <Button
            data-testid="timer-subject-change-button"
            aria-label={t('transaction:goDetail')}
            type="text"
            icon={<HorizontalDots className="horizontal-dot-icon" />}
            maxwidth="14px"
            height="14px"
            onClick={handleTimerDetail}
          />
        </Flex>

        {/* Without a matter there is nothing to name here, and an empty row reads as a value
            the user is missing rather than one they have not picked yet. */}
        {timer?.matter && (
          <CardData
            type="transaction-unit-price-source"
            icon={<Briefcase />}
            title={t('transaction:transactionUnitPriceSource.matter')}
            content={
              timer?.matter?.principalCompany?.name ??
              timer?.matter?.principalPrivatePerson?.fullName ??
              ''
            }
            subcontent={formatMatterReference(timer.matter)}
            contentclass="lineclamp-1"
            subcontentclass="lineclamp-1"
          />
        )}

        <Divider />

        <Flex className="w-full card-cta-wrapper" align="center">
          <TimerContainer>
            <Button
              data-testid="timer-play-pause-button"
              aria-label={t(isRunning ? 'common:timers.pauseTimer' : 'common:timers.startTimer')}
              type="text"
              icon={isRunning ? <Pause /> : <Play />}
              onClick={() => toggleTimer()}
            />
            <Text data-testid="timer-time-display" $fontsize="13px" $color="title" $weight={500}>
              {formatTime(seconds)}
            </Text>
          </TimerContainer>
          {/* Only earns its place once the actions wrap above the timer; alongside them it
              would be a rule between two things already sitting side by side. */}
          <Divider className="timer-cta-divider" />
          <Flex align="center" className="card-actions">
            <Tooltip title={t('common:timers.reset')} placement="top" arrow={false}>
              <IconButton
                data-testid="timer-reset-button"
                aria-label={t('common:timers.reset')}
                icon={<Reset />}
                onClick={() => {
                  setResetTimerConfirmation(true);
                }}
              />
            </Tooltip>
            <Tooltip title={t('common:edit')} placement="top" arrow={false}>
              <IconButton
                data-testid="timer-edit-button"
                aria-label={t('common:edit')}
                icon={<Edit />}
                onClick={handleEdit}
              />
            </Tooltip>
            <Divider type="vertical" className="timer-divider" />
            <Tooltip title={t('common:timers.removeFiveMinutes')} placement="top" arrow={false}>
              <IconButton
                data-testid="timer-remove-five-minutes-button"
                aria-label={t('common:timers.removeFiveMinutes')}
                icon={<TimerRemove />}
                onClick={handleRemoveFiveMinutes}
              />
            </Tooltip>
            <Tooltip title={t('common:timers.addFiveMinutes')} placement="top" arrow={false}>
              <IconButton
                data-testid="timer-add-five-minutes-button"
                aria-label={t('common:timers.addFiveMinutes')}
                icon={<TimerAdd />}
                onClick={handleAddFiveMinutes}
              />
            </Tooltip>

            <Divider type="vertical" className="timer-divider" />
            <Tooltip title={t('common:timers.close')} placement="top" arrow={false}>
              <IconButton
                data-testid="timer-close-button"
                aria-label={t('common:timers.close')}
                icon={<CloseCircle />}
                onClick={() => setDeleteTimerConfirmation(true)}
              />
            </Tooltip>
            <Tooltip
              title={t('common:timers.saveTransactionAndClose')}
              placement="top"
              arrow={false}
            >
              <IconButton
                data-testid="timer-save-button"
                aria-label={t('common:timers.saveTransactionAndClose')}
                icon={<CircleTick />}
                onClick={handleAddTransaction}
              />
            </Tooltip>
          </Flex>
        </Flex>
      </TimerCardWrapper>
      <ConfirmModal
        type={deleteTimerConfirmation ? 'danger' : 'primary'}
        visible={resetTimerConfirmation || deleteTimerConfirmation}
        onClose={() => {
          setResetTimerConfirmation(false);
          setDeleteTimerConfirmation(false);
        }}
        icon={deleteTimerConfirmation ? <Delete /> : <Reset />}
        title={
          deleteTimerConfirmation
            ? t('common:timers.closeModalTitle')
            : t('common:timers.resetModalTitle')
        }
        subtitle={
          deleteTimerConfirmation
            ? t('common:timers.closeModalSubTitle')
            : t('common:timers.resetModalSubTitle')
        }
        cancelText={t('common:cancel')}
        confirmText={
          deleteTimerConfirmation ? t('common:timers.closeTimer') : t('common:timers.resetTimer')
        }
        onConfirm={deleteTimerConfirmation ? handleRemoveTimer : handleTimerReset}
      />
    </>
  );
};

export default TimerCard;
